import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppHttpService } from './app-http.service';
import { DailyReport, ReportActivity, ReportBidItem, ReportEquipment, ReportLabor, ReportMaterialDelivery } from '../models';
import { CommonService } from './common.service';
import { environment as ENV } from 'src/environments/environment';
import { DailyReportCacheService } from './daily-report-cache.service';

// TypeScript interfaces for picture API
interface DailyReportPicture {
  uuid: string;
  thumb_url: string;
  original_url: string;
  original_name?: string; // Original filename from upload
  latitude: number | null;
  longitude: number | null;
  direction: number | null;
  site_uuid: string | null;
  site_name: string | null;
  report_uuid: string | null;
  report_date: string; // YYYY-MM-DD
  source_type: 'report_bid_item' | 'report_activity' | 'report_activity_code_header' | 'report_equipment' | 'report_labor' | 'report_material_delivery' | 'report_internal_test' | 'issue';
  source_uuid: string;
  source_name: string | null;
  created_at: string; // ISO 8601
}

interface PicturesResponse {
  status: number;
  data: DailyReportPicture[];
  type: 'AttachmentList';
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface GetPicturesOptions {
  siteIds?: string[];
  sources?: string[];
  limit?: number;
  offset?: number;
  useCache?: boolean;
}

// Cooldown period for staleness checks (30 seconds)
// Now that we use lightweight API calls (~100KB vs 6MB), we can check more frequently
const STALENESS_CHECK_COOLDOWN_MS = 30 * 1000;

@Injectable({ providedIn: 'root' })
export class DailyReportService {
  // Track last staleness check time per project to avoid redundant API calls
  private lastStalenessCheckMap = new Map<string, number>();

  constructor(
    private appHttpService: AppHttpService,
    private commonService: CommonService,
    private httpClient: HttpClient,
    private cacheService: DailyReportCacheService
  ) {}

  getRecord(id: string, qp = {}) {
    return this.appHttpService.getService(`/v1/daily-report/${id}`, qp).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new DailyReport(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  delete(id: string) {
    this.appHttpService.deleteService(`/v1/daily-report/${id}`)
      .toPromise()
      .then(() => {
        this.commonService.notification('Report deleted successfully.', 'success');
        window.history.back();
      })
      .catch(() => {
        this.commonService.notification('Unable to delete report.', 'danger');
      });
  }

  /**
   * Saves ReportBidItem|ReportMaterialDelivery objects
   * @param type string
   * @param payload object
   * @param qp object
   */
  async saveLineItem(type: string, payload: any, dailyReport: DailyReport, qp = {}): Promise<any> {

    const path = `/v1/daily-report/${dailyReport.id}/line-item/${type}`;
    return await this.appHttpService.postService(path, payload, qp).toPromise()
      .then((resp: any) => {
        if (resp.data) {
          switch (resp.type) {
            case 'ReportBidItem':
              return new ReportBidItem(resp.data);
            case 'ReportMaterialDelivery':
              return new ReportMaterialDelivery(resp.data);
            case 'ReportActivity':
              return new ReportActivity(resp.data);
            case 'ReportLabor':
              return new ReportLabor(resp.data);
            case 'ReportEquipment':
              return new ReportEquipment(resp.data);
            default:
              return resp.data;
          }
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Saves dailyreport
   * @param dailyReport DailyReport
   * @param qp query-params
   * @return {Promise<DailyReport>}
   */
  save(dailyReport: DailyReport, payload: any = null, qp: any = {}): Promise<DailyReport> {

    if (!payload) {
      payload = dailyReport.toPayload(true);
    }
    return this.appHttpService.postService(`/v1/daily-report/${dailyReport.site.id}/draft`, payload, qp).toPromise()
      .then(resp => {
        return new DailyReport(resp.data);
      });
  }

  /**
   * Deletes dailyreport line item
   * @param type string
   * @param model ReportBidItem|ReportMaterialDelivery...
   * @param dailyReport DailyReport
   */
  deleteLineItem(type: string, model: any, dailyReport: DailyReport): Promise<any> {

    const path = `/v1/daily-report/${dailyReport.id}/line-item/${type}/${model.id}`;
    return this.appHttpService.deleteService(path).toPromise();
  }

  /**
   * Get pictures from daily reports with geo-location (with caching support)
   * @param projectId Project UUID
   * @param startDate Start date (YYYY-MM-DD)
   * @param endDate End date (YYYY-MM-DD)
   * @param options Optional filters (siteIds, sources, limit, offset, useCache)
   * @return Observable<PicturesResponse>
   */
  getPictures(
    projectId: string,
    startDate: string,
    endDate: string,
    options?: GetPicturesOptions
  ): Observable<PicturesResponse> {
    const useCache = options?.useCache !== false; // Default true

    return new Observable(observer => {
      (async () => {
        // 1. Try cache first
        if (useCache) {
          const cached = await this.cacheService.getCachedPictures(
            projectId,
            startDate,
            endDate,
            options?.siteIds
          );

          if (cached) {
            observer.next({
              status: 200,
              type: 'AttachmentList',
              data: cached,
              meta: {
                total: cached.length,
                limit: options?.limit || 1000,
                offset: options?.offset || 0
              }
            });
            observer.complete();
            return;
          }
        }

        // 2. Cache miss - fetch from API
        // Build query string manually to avoid bracket notation for arrays
        const queryParts: string[] = [
          `start_date=${startDate}`,
          `end_date=${endDate}`
        ];

        // Add array parameters without bracket notation
        if (options?.siteIds && options.siteIds.length > 0) {
          options.siteIds.forEach(id => {
            queryParts.push(`site_ids=${encodeURIComponent(id)}`);
          });
        }
        if (options?.sources && options.sources.length > 0) {
          options.sources.forEach(source => {
            queryParts.push(`sources=${encodeURIComponent(source)}`);
          });
        }
        if (options?.limit) {
          queryParts.push(`limit=${options.limit}`);
        }
        if (options?.offset) {
          queryParts.push(`offset=${options.offset}`);
        }

        const queryString = queryParts.join('&');
        const url = `${ENV.API_ENDPOINT}/v1/project/${projectId}/pictures?${queryString}`;

        // Use HttpClient directly to avoid double question mark
        this.httpClient.get<PicturesResponse>(url)
          .pipe(
            map((response: PicturesResponse) => {
              // Store in cache
              if (useCache && response.data.length > 0) {
                this.cacheService.setCachedPictures(
                  projectId,
                  response.data,
                  startDate,
                  endDate,
                  options?.siteIds
                );
              }
              return response;
            })
          )
          .subscribe(
            response => {
              observer.next(response);
              observer.complete();
            },
            error => {
              observer.error(error);
            }
          );
      })();
    });
  }

  /**
   * Prefetch pictures for large date range
   * Runs in background, doesn't block UI
   */
  prefetchPictures(
    projectId: string,
    startDate: string,
    endDate: string,
    siteIds?: string[]
  ): void {
    this.getPictures(projectId, startDate, endDate, {
      siteIds,
      sources: [
        'report_bid_item',
        'report_activity',
        'issue',
        'report_material_delivery',
        'report_internal_test',
        'report_labor',
        'report_equipment'
      ],
      limit: 1000,
      useCache: true
    }).subscribe();
  }

  /**
   * Fetch daily reports in bulk for date range
   * Uses new optimized endpoint with caching and incremental updates
   */
  getBulkDailyReports(
    projectId: string,
    startDate: string,
    endDate: string,
    options?: {
      include?: any[];
      total?: number; // Records per page
      useCache?: boolean;
    }
  ): Observable<any[]> {
    const useCache = options?.useCache !== false; // Default true

    return new Observable(observer => {
      (async () => {
        // 1. Try cache first
        if (useCache) {
          const cached = await this.cacheService.getCachedReports(
            projectId,
            startDate,
            endDate
          );

          if (cached) {
            // Check for stale cache in background
            const cachedMaxUpdatedAt = await this.cacheService.getCachedMaxUpdatedAt(projectId);
            if (cachedMaxUpdatedAt) {
              // Fire and forget - check staleness and refresh if needed
              this.checkAndInvalidateStaleCache(
                projectId,
                startDate,
                endDate,
                cachedMaxUpdatedAt,
                options?.include
              );
            }

            observer.next(cached);
            observer.complete();
            return;
          }
        }

        // 3. Cache miss - fetch from API
        this.fetchReportsFromApi(projectId, startDate, endDate, options).subscribe(
          reports => {
            observer.next(reports);
            observer.complete();
          },
          error => observer.error(error)
        );
      })();
    });
  }

  /**
   * Check if cache is stale by comparing maxUpdatedAt with server
   * If stale, invalidates cache so next request fetches fresh data
   * Runs in background, doesn't block the UI
   * Uses cooldown to avoid redundant API calls on page refresh
   */
  private async checkAndInvalidateStaleCache(
    projectId: string,
    startDate: string,
    endDate: string,
    cachedMaxUpdatedAt: string,
    include?: any[]
  ): Promise<void> {
    try {
      // Check cooldown - skip if we checked recently
      const cacheKey = `${projectId}:${startDate}:${endDate}`;
      const lastCheck = this.lastStalenessCheckMap.get(cacheKey);
      const now = Date.now();

      if (lastCheck && (now - lastCheck) < STALENESS_CHECK_COOLDOWN_MS) {
        // Skip - checked too recently
        return;
      }

      // Update last check time
      this.lastStalenessCheckMap.set(cacheKey, now);

      // Fetch lightweight report list with minimal includes to get server's maxUpdatedAt
      // IMPORTANT: Must pass at least one include param, otherwise API uses defaultInclude (6MB+)
      const params: any = {
        from: startDate,
        to: endDate,
        total: 1000, // Get all reports to find true max
        start: 0,
        'include[0]': 'site' // Minimal include - just site relation, avoids defaultInclude fallback
      };

      const response: any = await this.appHttpService
        .getService(`/v1/daily-report/project/${projectId}`, params)
        .toPromise();

      const serverReports = response.data || [];

      if (serverReports.length === 0) {
        return;
      }

      // Compute server's maxUpdatedAt
      const serverMaxUpdatedAt = this.computeMaxUpdatedAt(serverReports);

      if (!serverMaxUpdatedAt) {
        return;
      }

      // Compare timestamps
      const cachedDate = new Date(cachedMaxUpdatedAt);
      const serverDate = new Date(serverMaxUpdatedAt);

      if (serverDate > cachedDate) {
        // Cache is stale - fetch full data with includes and update cache
        const fullParams: any = {
          from: startDate,
          to: endDate,
          total: 1000,
          start: 0
        };

        if (include) {
          this.addIncludeParams(fullParams, include);
        }

        const fullResponse: any = await this.appHttpService
          .getService(`/v1/daily-report/project/${projectId}`, fullParams)
          .toPromise();

        const fullReports = fullResponse.data || [];

        if (fullReports.length > 0) {
          await this.cacheService.setCachedReports(projectId, fullReports, startDate, endDate);
        }
      }
    } catch {
      // Silently fail - cache will be refreshed on expiry
    }
  }

  /**
   * Compute max updated_at from reports array
   */
  private computeMaxUpdatedAt(reports: any[]): string | null {
    if (!reports || reports.length === 0) return null;

    let maxDate: Date | null = null;

    for (const report of reports) {
      const updatedAt = report.updated_at;
      if (updatedAt) {
        const date = new Date(updatedAt);
        if (!maxDate || date > maxDate) {
          maxDate = date;
        }
      }
    }

    return maxDate ? maxDate.toISOString() : null;
  }

  /**
   * Fetch reports from API (no cache)
   */
  private fetchReportsFromApi(
    projectId: string,
    startDate: string,
    endDate: string,
    options?: {
      include?: any[];
      total?: number;
      useCache?: boolean;
    }
  ): Observable<any[]> {
    const params: any = {
      from: startDate,
      to: endDate,
      total: options?.total || 1000,
      start: 0
    };

    if (options?.include) {
      this.addIncludeParams(params, options.include);
    }

    return this.appHttpService
      .getService(`/v1/daily-report/project/${projectId}`, params)
      .pipe(
        map((response: any) => {
          const reports = response.data || [];

          // Store in cache
          if (options?.useCache !== false && reports.length > 0) {
            this.cacheService.setCachedReports(
              projectId,
              reports,
              startDate,
              endDate
            );
          }

          return reports;
        })
      );
  }

  /**
   * Helper to add include params to request
   */
  private addIncludeParams(params: any, include: any[]): void {
    const processInclude = (item: any, path: string) => {
      if (typeof item === 'string') {
        params[path] = item;
      } else if (typeof item === 'object' && !Array.isArray(item)) {
        const key = Object.keys(item)[0];
        const value = item[key];
        if (Array.isArray(value)) {
          value.forEach((v, vi) => {
            processInclude(v, `${path}[${key}][${vi}]`);
          });
        } else {
          params[`${path}[${key}]`] = value;
        }
      }
    };

    include.forEach((inc, index) => {
      processInclude(inc, `include[${index}]`);
    });
  }

  /**
   * Prefetch reports for large date range
   * Runs in background, doesn't block UI
   */
  prefetchReports(
    projectId: string,
    startDate: string,
    endDate: string
  ): void {
    console.log('🔄 Prefetching reports in background...');

    this.getBulkDailyReports(projectId, startDate, endDate, {
      include: [
        'site',
        'createdBy',  // Include creator info for inspector filtering (camelCase for API)
        {
          bid_items: [
            'map_feature_link',
            'sources',
            {
              bid_item: ['fields']  // Include fields for rollup bid items
            },
            'heading',
            'station',
            'pictures'
          ]
        },
        {
          activities: ['map_feature_link', 'pictures']  // Include activities with pictures
        },
        {
          material_deliveries: [
            'map_feature_link',
            'pictures',
            'tickets',
            'bid_item',
            'material',
            { annotations: ['drawing'] },
            'field_values'
          ]
        },
        // The report cache is keyed by projectId only (last-write-wins). This
        // background prefetch runs over the whole project timeline and commonly
        // lands AFTER the visible-range fetch, so it must carry control_forms too —
        // otherwise it overwrites the cache with forms-less reports and the
        // forms-on-map group toggle/markers disappear intermittently.
        {
          control_forms: ['map_feature_link', 'control_form', 'pictures', 'lines']
        },
        { site_geometries: [] }
      ],
      total: 1000,
      useCache: true
    }).subscribe(
      reports => console.log('✅ Prefetch complete:', reports.length, 'reports'),
      error => console.error('❌ Prefetch failed:', error)
    );
  }

  /**
   * Clear cache and force refresh
   */
  async refreshReports(projectId: string): Promise<void> {
    await this.cacheService.clearAllCaches(projectId);
  }

}
